import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, theme, Alert, Form } from 'antd';
import FormSelect from '../../../../../../antd/form/FormSelect/FormSelect.jsx';
import { required } from '../../../../../../antd/form/FormValidation.js';
import { useGetErasmusUniversitiesQuery } from '../../../../../../../redux/services/erasmusUniversities';
import FormSchoolYearPicker from '../../../../../../antd/form/FormSchoolYearPicker/FormSchoolYearPicker.jsx';

// utils
const removeDiacritics = (s = '') =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
const escapeReg = (s = '') => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const highlight = (text, q) => {
  if (!q) return text;
  const re = new RegExp(`(${escapeReg(q)})`, 'ig');
  return String(text)
    .split(re)
    .map((part, i) => (re.test(part) ? <mark key={i}>{part}</mark> : part));
};

const RecievingInstitution = ({ languages, isPreview, selectedChallenge }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const form = Form.useFormInstance?.();
  const { data: erasmusUniversities } = useGetErasmusUniversitiesQuery(true);

  //sledovanie vybraných hodnôt pre zakaz duplicíit
  const uni1 = Form.useWatch?.('university1_id', form);
  const uni2 = Form.useWatch?.('university2_id', form);
  const uni3 = Form.useWatch?.('university3_id', form);
  const selectedIds = useMemo(
    () => [uni1, uni2, uni3].filter(Boolean),
    [uni1, uni2, uni3],
  );

  // query pre zvýraznenie
  const [query, setQuery] = useState('');

  //css styly pre tabuľku
  const styles = useMemo(
    () => ({
      row: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr',
        gap: 8,
        alignItems: 'center',
      },
      colName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 500,
      },
      col: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      headerWrap: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        fontWeight: 600,
        padding: '6px 12px',
        borderBottom: `1px solid ${token.colorBorder}`,
        marginBottom: 4,
        background: token.colorBgElevated,
        color: token.colorText,
      },
    }),
    [token],
  );

  // Filter universities based on selected challenge
  const availableUniversities = useMemo(() => {
    if (!selectedChallenge || !erasmusUniversities) {
      return erasmusUniversities || [];
    }

    // If challenge has a consortium, get universities from consortium
    if (
      selectedChallenge.consortium_id &&
      selectedChallenge.consortium?.universities
    ) {
      const consortiumUniversityIds =
        selectedChallenge.consortium.universities.map((u) => u.id);
      return erasmusUniversities.filter((u) =>
        consortiumUniversityIds.includes(u.id),
      );
    }

    // If challenge has a single university, return only that university
    if (selectedChallenge.university_id) {
      return erasmusUniversities.filter(
        (u) => u.id === selectedChallenge.university_id,
      );
    }

    // Fallback: return all universities if challenge doesn't have proper data
    return erasmusUniversities;
  }, [selectedChallenge, erasmusUniversities]);

  // options: label je STRING (kvôli stabilnému filtru), render  cez optionRender
  const universityOptions = useMemo(() => {
    const list = Array.isArray(availableUniversities)
      ? availableUniversities
      : [];
    return list.map((u) => {
      const name = u?.name ?? '';
      const city = u?.city ?? '';
      const country = u?.country ?? '';
      return {
        value: u.id,
        label: `${name} (${city}, ${country})`, // zobrazenie v inpute + zdroj pre filter
        name,
        city,
        country, //použijeme v optionRender
        disabled: selectedIds.includes(u.id), // zákaz duplicít medzi 1/2/3
      };
    });
  }, [availableUniversities, selectedIds]);

  const languageOptions = useMemo(
    () =>
      languages
        ? Object.entries(languages).map(([code, name]) => ({
            value: code,
            label: name,
          }))
        : [],
    [languages],
  );

  const languageKnowlegeOptions = [
    { value: 'A1', label: t('A1 (Začiatočník)') },
    { value: 'A2', label: t('A2 (Mierne pokročilý)') },
    { value: 'B1', label: t('B1 (Stredne pokročilý)') },
    { value: 'B2', label: t('B2 (Vyššie stredne pokročilý)') },
    { value: 'C1', label: t('C1 (Pokročilý)') },
    { value: 'C2', label: t('C2 (Expert)') },
  ];

  // spoločná konfigurácia pre 3 selecty
  const uniSelectProps = {
    options: universityOptions,
    disabled: isPreview,
    showSearch: true,
    virtual: true,
    popupMatchSelectWidth: true,
    notFoundContent: t('Nenašli sa žiadne univerzity'),
    onSearch: (val) => setQuery(val),

    // filter bez diakritiky nad STRING labelom
    filterOption: (input, option) => {
      const q = removeDiacritics(input || '');
      const hay = removeDiacritics(String(option?.label ?? ''));
      return !q || hay.includes(q);
    },

    // 3-stĺpcový render položky + zvýraznenie zhody
    optionRender: (option) => {
      let { name, city, country } = option;
      if (!name) {
        // ak by wrapper odsekol custom polia skúsi parse z labelu
        const m = String(option?.label ?? '').match(
          /^(.*)\s+\((.*),\s*(.*)\)$/,
        );
        if (m) {
          name = m[1];
          city = m[2];
          country = m[3];
        }
      }
      return (
        <div style={styles.row}>
          <span style={styles.colName} title={name}>
            {highlight(name, query)}
          </span>
          <span style={styles.col} title={city}>
            {highlight(city, query)}
          </span>
          <span style={styles.col} title={country}>
            {highlight(country, query)}
          </span>
        </div>
      );
    },

    // hlavicka tabušky zostane navrchu pri scrollovaní
    popupRender: (menu) => (
      <>
        <div style={styles.headerWrap}>
          <div style={styles.row}>
            <span>{t('Názov')}</span>
            <span>{t('Mesto')}</span>
            <span>{t('Krajina')}</span>
          </div>
        </div>
        {menu}
      </>
    ),
  };

  return (
    <>
      <FormSelect
        label={t('Univerzita 1')}
        name="university1_id"
        rules={[required(t)]}
        {...uniSelectProps}
      />

      <FormSelect
        label={t('Univerzita 2')}
        name="university2_id"
        {...uniSelectProps}
      />

      <FormSelect
        label={t('Univerzita 3')}
        name="university3_id"
        {...uniSelectProps}
      />

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <FormSchoolYearPicker
            label={t('Rok mobility')}
            name="mobility_year"
            rules={[required(t)]}
            disabled={isPreview}
          />
        </Col>
        <Col span={12}>
          <FormSelect
            label={t('Semester mobility')}
            name="mobility_semester"
            options={[
              { label: t('Zimný'), value: 'zimný' },
              { label: t('Letný'), value: 'letný' },
            ]}
            rules={[required(t)]}
            disabled={isPreview}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <FormSelect
            label={t('Jazyk mobility')}
            name="study_language"
            options={languageOptions}
            rules={[required(t)]}
            disabled={isPreview}
          />
        </Col>
        <Col span={12}>
          <FormSelect
            label={t('Jazykové znalosti')}
            name="language_knowledge"
            options={languageKnowlegeOptions}
            rules={[required(t)]}
            disabled={isPreview}
          />
        </Col>
      </Row>

      <Alert
        title={t(
          'Ak univerzita, na ktorú sa chcete prihlásiť, nie je v zozname dostupných univerzít, kontaktujte prosím Erasmus koordinátora pre ďalšie informácie.',
        )}
        type="info"
        showIcon
        style={{ marginBottom: 10 }}
      />
      <Alert
        title={t(
          'Univerzity si vyberáte na základe priority. Univerzita 1 má najvyššiu prioritu, Univerzita 3 najnižšiu prioritu.',
        )}
        type="info"
        showIcon
        style={{ marginBottom: 10 }}
      />
    </>
  );
};

export default RecievingInstitution;
